/**
 * Bridge linking persistence utilities.
 * Used for tracking bridge discovery linking requests.
 */

import * as persistenceLayer from '#~/persistence/index.js';
import { logger } from '#~/logger/index.js';

export const LINKING_REQUEST_KEY_PREFIX = 'bridge_linking_request';

export interface BridgeLinkingRequest {
  serviceHost: string;
  requestedAt: number;
}

/**
 * Set or clear a bridge linking request in persistent storage.
 */
export const setLinkingRequest = async (
  request: BridgeLinkingRequest | null,
  serviceHost: string
): Promise<void> => {
  const key = `${LINKING_REQUEST_KEY_PREFIX}:${serviceHost}`;
  if (request) {
    await persistenceLayer.set(key, JSON.stringify(request));
  } else {
    await persistenceLayer.remove(key);
  }
};

/**
 * Get all bridge linking requests from persistent storage.
 */
export const getAllLinkingRequests = async (): Promise<
  Record<string, BridgeLinkingRequest> | undefined
> => {
  // Canonical keys() is unprefixed string[]. Object.entries on that array
  // used to treat "0" as the key and skip restore after refresh.
  const allKeys = await persistenceLayer.keys();
  const requests: Record<string, BridgeLinkingRequest> = {};

  for (const key of allKeys) {
    if (!key.startsWith(LINKING_REQUEST_KEY_PREFIX)) {
      continue;
    }
    try {
      const data = await persistenceLayer.get(key);
      if (data) {
        const parsed = JSON.parse(data) as BridgeLinkingRequest;
        requests[parsed.serviceHost] = parsed;
      }
    } catch (error) {
      // See knowledge: use-canonical-logger-not-console (ai.knowledge/studio.client)
      logger.error('Error parsing linking request:', error);
    }
  }

  return Object.keys(requests).length > 0 ? requests : undefined;
};
